import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BracketConnectors from "./BracketConnectors";
import BracketRound from "./BracketRound";
import { playerKnockoutPath } from "./searchHelpers";
import type { PlayerDirectory, Round } from "./types";
import styles from "./Resultats.module.css";

interface BracketTreeProps {
  rounds: Round[];
  players: PlayerDirectory;
  bracketLinkMode?: string | null;
  highlightedLicence?: string | null;
  onSelectLicence?: (licence: string) => void;
}

export default function BracketTree({
  rounds,
  players,
  bracketLinkMode = null,
  highlightedLicence = null,
  onSelectLicence,
}: BracketTreeProps): React.ReactElement {
  const connected = bracketLinkMode === "ROUND_ORDER";
  const pathMatchIds = useMemo(
    () =>
      highlightedLicence
        ? playerKnockoutPath(rounds, highlightedLicence)
        : new Set<string>(),
    [rounds, highlightedLicence]
  );
  const firstRoundMatchCount = rounds[0]?.matches.length ?? 0;
  const layoutKey = `${highlightedLicence ?? ""}|${rounds
    .map((r) => r.id)
    .join(",")}`;

  return (
    <Box className={styles.bracketTreeWrap}>
      {!connected && rounds.length > 1 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, opacity: 0.8 }}
        >
          Liens entre tours non disponibles — affichage par colonnes.
        </Typography>
      )}
      <Box
        className={`${styles.bracketScroll}${
          connected ? ` ${styles.bracketScrollConnected}` : ""
        }`}
      >
        {connected && (
          <BracketConnectors
            rounds={rounds}
            connected={connected}
            pathMatchIds={pathMatchIds}
            layoutKey={layoutKey}
          />
        )}
        {rounds.map((round) => (
          <BracketRound
            key={round.id}
            round={round}
            players={players}
            highlightedLicence={highlightedLicence}
            pathMatchIds={pathMatchIds}
            onSelectLicence={onSelectLicence}
            firstRoundMatchCount={
              connected ? firstRoundMatchCount : undefined
            }
          />
        ))}
      </Box>
    </Box>
  );
}
